/* ==========================================================================
   TheraMotion — global.js
   Core runtime: utilities, drawers, AJAX cart, product form, variant picker,
   quantity, header, announcement, accordions, reveal, predictive search.
   No dependencies.
   ========================================================================== */
(() => {
  const DL = (window.DL = window.DL || {});
  DL.routes = DL.routes || { root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', cartUpdate: '/cart/update' };
  DL.strings = DL.strings || {};
  DL.settings = DL.settings || {};
  DL.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Utilities ---------- */
  DL.qs = (sel, root = document) => root.querySelector(sel);
  DL.qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  DL.debounce = (fn, wait = 200) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };
  DL.emit = (name, detail = {}) => document.dispatchEvent(new CustomEvent(`dl:${name}`, { detail }));
  DL.on = (name, cb) => document.addEventListener(`dl:${name}`, cb);

  DL.formatMoney = (cents, format = DL.moneyFormat || '{{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const group = (n, precision, thousands = ',', decimal = '.') => {
      const [int, dec] = (n / 100).toFixed(precision).split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? grouped + decimal + dec : grouped;
    };
    const match = format.match(placeholder);
    const key = match ? match[1] : 'amount';
    const formatters = {
      amount: () => group(cents, 2),
      amount_no_decimals: () => group(cents, 0),
      amount_with_comma_separator: () => group(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => group(cents, 0, '.', ','),
      amount_with_apostrophe_separator: () => group(cents, 2, "'", '.'),
      amount_no_decimals_with_space_separator: () => group(cents, 0, ' ', ''),
      amount_with_space_separator: () => group(cents, 2, ' ', ','),
      amount_with_period_and_space_separator: () => group(cents, 2, ' ', '.')
    };
    let value = (formatters[key] || formatters.amount)();
    value = value.replace(/[.,]00$/, ''); // mirrors money_without_trailing_zeros
    return format.replace(placeholder, value);
  };

  DL.announce = (message) => {
    const region = DL.qs('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => (region.textContent = message), 60);
  };

  let toastTimer;
  DL.toast = (message) => {
    const toast = DL.qs('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2600);
  };

  DL.focusable = (root) =>
    DL.qsa(
      'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary',
      root
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);

  /* ---------- Drawer ---------- */
  class DLDrawer extends HTMLElement {
    connectedCallback() {
      // Drawers declared inside sticky/transformed sections are moved to <body>
      // so they always stack above page UI (e.g. the mobile sticky bar).
      if (this.hasAttribute('data-portal') && this.parentElement !== document.body) {
        DL.qsa(`body > dl-drawer[id="${this.id}"]`).forEach((stale) => stale !== this && stale.remove());
        document.body.appendChild(this);
        return;
      }
      if (this._ready) return;
      this._ready = true;
      this.panel = this.querySelector('.dl-drawer');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) {
          e.preventDefault();
          this.close();
        }
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trapFocus(e);
      });
    }
    get isOpen() {
      return this.hasAttribute('open');
    }
    open(opener) {
      if (this.isOpen) return;
      DL.qsa('dl-drawer[open]').forEach((d) => d !== this && d.close(false));
      this.opener = opener || document.activeElement;
      this.setAttribute('open', '');
      document.documentElement.classList.add('dl-lock');
      this.panel && this.panel.setAttribute('aria-hidden', 'false');
      DL.qsa('[aria-controls="' + this.id + '"]').forEach((el) => el.setAttribute('aria-expanded', 'true'));
      window.setTimeout(() => {
        const target = this.querySelector('[data-autofocus]') || this.panel;
        target && target.focus({ preventScroll: true });
      }, 60);
      DL.emit('drawer:open', { id: this.id });
    }
    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.removeAttribute('open');
      this.panel && this.panel.setAttribute('aria-hidden', 'true');
      DL.qsa('[aria-controls="' + this.id + '"]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
      if (!DL.qs('dl-drawer[open]')) document.documentElement.classList.remove('dl-lock');
      if (restoreFocus && this.opener && typeof this.opener.focus === 'function') this.opener.focus({ preventScroll: true });
      DL.emit('drawer:close', { id: this.id });
    }
    trapFocus(e) {
      if (!this.isOpen) return;
      const items = DL.focusable(this.panel);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  if (!customElements.get('dl-drawer')) customElements.define('dl-drawer', DLDrawer);

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.dataset.drawerOpen);
    if (drawer && typeof drawer.open === 'function') {
      e.preventDefault();
      drawer.open(trigger);
    }
  });

  /* ---------- Cart API ---------- */
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const cartSections = () => {
    const ids = ['cart-drawer'];
    if (DL.qs('[data-cart-page]')) ids.push(DL.qs('[data-cart-page]').dataset.sectionId);
    return ids;
  };

  DL.cart = {
    async get() {
      const res = await fetch(`${DL.routes.cart}.js`, { headers: { Accept: 'application/json' } });
      return res.json();
    },
    async request(url, body) {
      const payload = Object.assign({}, body, { sections: cartSections(), sections_url: window.location.pathname });
      const res = await fetch(url, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok || data.status) {
        const err = new Error(data.description || data.message || DL.strings.cartError);
        err.data = data;
        throw err;
      }
      return data;
    },
    add(items) {
      return this.request(`${DL.routes.cartAdd}.js`, { items });
    },
    change(key, quantity) {
      return this.request(`${DL.routes.cartChange}.js`, { id: key, quantity });
    },
    async update(body) {
      const res = await fetch(`${DL.routes.cartUpdate}.js`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(body) });
      return res.json();
    },
    async refresh() {
      const ids = cartSections().join(',');
      const res = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(ids)}`);
      const sections = await res.json();
      DL.cart.render(sections);
      return sections;
    },
    render(sections) {
      if (!sections) return;
      Object.keys(sections).forEach((id) => {
        const html = sections[id];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        DL.qsa(`[data-cart-render="${id}"]`).forEach((target) => {
          const source = doc.querySelector(`[data-cart-render="${id}"]`);
          if (!source) return;
          const active = document.activeElement;
          const activeId = active && target.contains(active) ? active.id : null;
          const scroller = target.querySelector('.dl-drawer__body');
          const scrollTop = scroller ? scroller.scrollTop : 0;
          target.innerHTML = source.innerHTML;
          const newScroller = target.querySelector('.dl-drawer__body');
          if (newScroller) newScroller.scrollTop = scrollTop;
          if (activeId || (active && !document.body.contains(active))) {
            const next = (activeId && document.getElementById(activeId)) || target.closest('.dl-drawer') || target;
            next.focus({ preventScroll: true });
          }
        });
        const counter = doc.querySelector('[data-cart-count]');
        if (counter) DL.cart.setCount(parseInt(counter.dataset.cartCount, 10) || 0);
      });
      DL.emit('cart:rendered');
    },
    setCount(count) {
      DL.qsa('[data-cart-bubble]').forEach((bubble) => {
        const previous = bubble.textContent;
        bubble.textContent = count > 0 ? String(count) : '';
        if (previous !== bubble.textContent && count > 0) {
          bubble.classList.remove('is-bump');
          void bubble.offsetWidth;
          bubble.classList.add('is-bump');
        }
      });
      DL.qsa('[data-cart-label]').forEach((el) => {
        const tpl = el.dataset.cartLabel;
        if (tpl) el.setAttribute('aria-label', tpl.replace('[count]', count));
      });
    },
    open(opener) {
      const drawer = document.getElementById('CartDrawer');
      if (drawer && DL.cartType === 'drawer') drawer.open(opener);
      else if (!DL.qs('[data-cart-page]')) window.location.href = DL.routes.cart;
    }
  };

  /* Cart line interactions (drawer + cart page), delegated */
  const setLineBusy = (key, busy) => {
    DL.qsa(`[data-line-key="${CSS.escape(key)}"]`).forEach((line) => line.classList.toggle('is-updating', busy));
  };
  const changeLine = async (key, quantity) => {
    setLineBusy(key, true);
    try {
      const data = await DL.cart.change(key, quantity);
      DL.cart.render(data.sections);
      DL.emit('cart:updated', { cart: data });
      DL.announce(DL.strings.cartUpdated || '');
    } catch (err) {
      setLineBusy(key, false);
      const line = DL.qs(`[data-line-key="${CSS.escape(key)}"]`);
      const holder = line && line.querySelector('[data-line-error]');
      if (holder) {
        holder.textContent = err.message || DL.strings.cartError;
        holder.hidden = false;
      } else {
        DL.toast(err.message || DL.strings.cartError);
      }
    }
  };

  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-line-remove]');
    if (remove) {
      e.preventDefault();
      changeLine(remove.dataset.lineRemove, 0);
      return;
    }
    const step = e.target.closest('[data-line-step]');
    if (step) {
      const line = step.closest('[data-line-key]');
      const input = line && line.querySelector('[data-line-qty]');
      if (!input) return;
      const next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.dataset.lineStep, 10));
      input.value = next;
      changeLine(line.dataset.lineKey, next);
    }
  });
  document.addEventListener(
    'change',
    (e) => {
      const input = e.target.closest('[data-line-qty]');
      if (input) {
        const line = input.closest('[data-line-key]');
        changeLine(line.dataset.lineKey, Math.max(0, parseInt(input.value, 10) || 0));
      }
      const note = e.target.closest('[data-cart-note]');
      if (note) DL.cart.update({ note: note.value });
    },
    true
  );

  window.addEventListener('pageshow', (e) => {
    if (e.persisted) DL.cart.get().then((cart) => DL.cart.setCount(cart.item_count)).catch(() => {});
  });

  /* ---------- Buttons: loading / success states ---------- */
  DL.buttonState = (buttons, state) => {
    buttons.forEach((btn) => {
      if (!btn) return;
      btn.classList.remove('is-loading', 'is-done');
      if (state === 'loading') {
        btn.classList.add('is-loading');
        btn.setAttribute('aria-busy', 'true');
      } else {
        btn.removeAttribute('aria-busy');
        if (state === 'done') {
          btn.classList.add('is-done');
          window.setTimeout(() => btn.classList.remove('is-done'), 1600);
        }
      }
    });
  };

  /* ---------- Product form (AJAX add to cart) ---------- */
  class DLProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.errorEl = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    get buttons() {
      const own = DL.qsa('[type="submit"]', this.form);
      const external = this.form.id ? DL.qsa(`button[form="${this.form.id}"]`) : [];
      return own.concat(external);
    }
    async onSubmit(e) {
      if (DL.cartType !== 'drawer' && !this.hasAttribute('data-always-ajax')) return;
      e.preventDefault();
      const buttons = this.buttons;
      if (buttons.some((b) => b.getAttribute('aria-disabled') === 'true' || b.disabled)) return;
      this.showError('');
      DL.buttonState(buttons, 'loading');
      const fd = new FormData(this.form);
      const item = { id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity') || '1', 10) || 1 };
      if (fd.get('selling_plan')) item.selling_plan = parseInt(fd.get('selling_plan'), 10);
      const properties = {};
      for (const [key, value] of fd.entries()) {
        const m = key.match(/^properties\[(.+)\]$/);
        if (m && value !== '') properties[m[1]] = value;
      }
      if (Object.keys(properties).length) item.properties = properties;
      try {
        const data = await DL.cart.add([item]);
        DL.cart.render(data.sections);
        DL.buttonState(buttons, 'done');
        const title = data.items && data.items[0] ? data.items[0].product_title : '';
        DL.announce((DL.strings.itemAdded || DL.strings.added || '').replace('[title]', title));
        DL.emit('cart:added', { items: data.items });
        if (!this.hasAttribute('data-no-open')) {
          window.setTimeout(() => DL.cart.open(buttons[0]), DL.reduceMotion ? 0 : 280);
        } else {
          DL.toast(DL.strings.added);
        }
      } catch (err) {
        DL.buttonState(buttons, 'idle');
        this.showError(err.message || DL.strings.cartError);
      }
    }
    showError(message) {
      if (!this.errorEl) {
        if (message) DL.toast(message);
        return;
      }
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }
  }
  if (!customElements.get('dl-product-form')) customElements.define('dl-product-form', DLProductForm);

  /* ---------- Variant picker ---------- */
  class DLVariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.scope = this.closest('[data-product-scope]') || document;
      this.updateUrl = this.hasAttribute('data-update-url');
      this.addEventListener('change', () => this.onChange());
      this.markAvailability();
    }
    selectedOptions() {
      return DL.qsa('[data-option-index]', this)
        .sort((a, b) => a.dataset.optionIndex - b.dataset.optionIndex)
        .map((group) => {
          const checked = group.querySelector('input:checked');
          const select = group.querySelector('select');
          return checked ? checked.value : select ? select.value : null;
        });
    }
    onChange() {
      const options = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((val, i) => val === options[i]));
      this.updateLabels(options);
      this.markAvailability();
      this.apply(variant);
    }
    updateLabels(options) {
      DL.qsa('[data-option-index]', this).forEach((group, i) => {
        const label = group.querySelector('[data-selected-value]');
        if (label) label.textContent = options[i];
      });
    }
    markAvailability() {
      const options = this.selectedOptions();
      DL.qsa('[data-option-index]', this).forEach((group) => {
        const index = parseInt(group.dataset.optionIndex, 10);
        DL.qsa('input[type="radio"]', group).forEach((input) => {
          const candidate = options.slice();
          candidate[index] = input.value;
          const match = this.variants.find((v) => v.options.every((val, i) => val === candidate[i]));
          const available = !!(match && match.available);
          input.closest('[data-option-value]')?.classList.toggle('is-unavailable', !available);
          input.dataset.available = available;
        });
      });
    }
    apply(variant) {
      const forms = DL.qsa(`form[data-product-form="${this.dataset.productId}"]`, this.scope);
      const buttons = [];
      forms.forEach((form) => {
        const idInput = form.querySelector('input[name="id"]');
        if (idInput && variant) idInput.value = variant.id;
        buttons.push(...DL.qsa('[type="submit"]', form));
        if (form.id) buttons.push(...DL.qsa(`button[form="${form.id}"]`));
      });
      buttons.forEach((btn) => {
        const label = btn.querySelector('.dl-btn__label');
        const priceEl = btn.querySelector('.dl-btn__price');
        const sep = btn.querySelector('.dl-btn__sep');
        if (!variant) {
          btn.setAttribute('aria-disabled', 'true');
          if (label) label.textContent = DL.strings.unavailable;
        } else if (!variant.available) {
          btn.setAttribute('aria-disabled', 'true');
          if (label) label.textContent = DL.strings.soldOut;
        } else {
          btn.removeAttribute('aria-disabled');
          if (label) label.textContent = btn.dataset.label || DL.strings.addToCart;
        }
        if (priceEl) {
          priceEl.hidden = !variant || !variant.available;
          if (variant) priceEl.textContent = DL.formatMoney(variant.price);
        }
        if (sep) sep.hidden = !variant || !variant.available;
      });
      if (variant) {
        DL.qsa('[data-price-block]', this.scope).forEach((block) => DL.renderPrice(block, variant));
        if (this.updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }
      DL.emit('variant:change', { productId: this.dataset.productId, variant, scope: this.scope, source: this });
    }
  }
  if (!customElements.get('dl-variant-picker')) customElements.define('dl-variant-picker', DLVariantPicker);

  DL.renderPrice = (block, variant) => {
    const current = block.querySelector('[data-price]');
    const compare = block.querySelector('[data-compare]');
    const compareLabel = block.querySelector('[data-compare-label]');
    const saving = block.querySelector('[data-saving]');
    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    if (current) current.textContent = DL.formatMoney(variant.price);
    if (compare) {
      compare.hidden = !onSale;
      compare.textContent = onSale ? DL.formatMoney(variant.compare_at_price) : '';
    }
    if (compareLabel) compareLabel.hidden = !onSale;
    if (saving) {
      saving.hidden = !onSale;
      if (onSale) {
        const diff = variant.compare_at_price - variant.price;
        saving.textContent = saving.dataset.template
          ? saving.dataset.template
              .replace('[amount]', DL.formatMoney(diff))
              .replace('[percent]', Math.round((diff * 100) / variant.compare_at_price))
          : DL.settings.savings === 'percent'
            ? (DL.strings.savePercent || '−[percent] %').replace('[percent]', Math.round((diff * 100) / variant.compare_at_price))
            : (DL.strings.saveAmount || '[amount]').replace('[amount]', DL.formatMoney(diff));
      }
    }
  };

  /* ---------- Quantity stepper ---------- */
  class DLQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-step]');
        if (!btn || !this.input) return;
        e.preventDefault();
        const min = parseInt(this.input.min || '1', 10);
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || min) + parseInt(btn.dataset.step, 10)));
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
        this.sync();
      });
      this.input && this.input.addEventListener('change', () => this.sync());
      this.sync();
    }
    sync() {
      if (!this.input) return;
      const value = parseInt(this.input.value, 10) || 1;
      const min = parseInt(this.input.min || '1', 10);
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      const minus = this.querySelector('[data-step="-1"]');
      const plus = this.querySelector('[data-step="1"]');
      if (minus) minus.disabled = value <= min;
      if (plus) plus.disabled = value >= max;
      const form = this.input.form;
      if (form) {
        DL.qsa('[data-qty-pick]', form.closest('[data-product-scope]') || document).forEach((pick) => {
          pick.setAttribute('aria-pressed', String(parseInt(pick.dataset.qtyPick, 10) === value));
        });
      }
    }
  }
  if (!customElements.get('dl-quantity')) customElements.define('dl-quantity', DLQuantity);

  document.addEventListener('click', (e) => {
    const pick = e.target.closest('[data-qty-pick]');
    if (!pick) return;
    const scope = pick.closest('[data-product-scope]') || document;
    const input = scope.querySelector('dl-quantity input');
    if (!input) return;
    input.value = pick.dataset.qtyPick;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Header ---------- */
  class DLHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.sticky || 'always';
      this.section = this.closest('.shopify-section') || this;
      this.section.classList.toggle('dl-header-section--static', this.mode === 'none');
      this.lastY = window.scrollY;
      this.ticking = false;
      this.setHeight();
      window.addEventListener('scroll', () => this.onScroll(), { passive: true });
      window.addEventListener('resize', DL.debounce(() => this.setHeight(), 150));
      this.onScroll();
    }
    setHeight() {
      const h = this.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--header-live', `${Math.round(h)}px`);
    }
    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        this.section.classList.toggle('is-scrolled', y > 4);
        if (this.mode === 'scroll_up' && !DL.qs('dl-drawer[open]')) {
          const down = y > this.lastY && y > 240;
          if (Math.abs(y - this.lastY) > 4) {
            this.section.classList.toggle('is-hidden', down);
            document.documentElement.classList.toggle('dl-header-hidden', down);
          }
        }
        this.lastY = y;
        this.ticking = false;
      });
    }
  }
  if (!customElements.get('dl-header')) customElements.define('dl-header', DLHeader);

  /* ---------- Announcement bar ---------- */
  class DLAnnouncement extends HTMLElement {
    connectedCallback() {
      this.slides = DL.qsa('[data-slide]', this);
      if (this.slides.length < 2) return;
      this.index = 0;
      this.delay = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      this.paused = false;
      ['mouseenter', 'focusin'].forEach((ev) => this.addEventListener(ev, () => (this.paused = true)));
      ['mouseleave', 'focusout'].forEach((ev) => this.addEventListener(ev, () => (this.paused = false)));
      this.timer = window.setInterval(() => this.next(), this.delay);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    next() {
      if (this.paused || document.hidden) return;
      if (window.matchMedia('(min-width: 990px)').matches && this.classList.contains('dl-announcement--inline')) return;
      this.slides[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.slides.length;
      this.slides[this.index].classList.add('is-active');
    }
  }
  if (!customElements.get('dl-announcement')) customElements.define('dl-announcement', DLAnnouncement);

  /* ---------- Accordions (animated <details>) ---------- */
  document.addEventListener('click', (e) => {
    const summary = e.target.closest('details.dl-accordion > summary');
    if (!summary) return;
    const details = summary.parentElement;
    const content = details.querySelector(':scope > .dl-accordion__content');
    if (!content || DL.reduceMotion || typeof content.animate !== 'function') return;
    e.preventDefault();
    if (details.dataset.animating) return;
    details.dataset.animating = 'true';
    const done = () => delete details.dataset.animating;
    if (!details.open) {
      details.open = true;
      const h = content.scrollHeight;
      const anim = content.animate(
        [
          { height: '0px', opacity: 0 },
          { height: `${h}px`, opacity: 1 }
        ],
        { duration: Math.min(520, 220 + h * 0.4), easing: 'cubic-bezier(.2,.7,.2,1)' }
      );
      anim.onfinish = done;
      const group = details.closest('[data-accordion-group]');
      if (group && group.dataset.accordionGroup === 'single') {
        DL.qsa('details.dl-accordion[open]', group).forEach((other) => {
          if (other !== details) other.open = false;
        });
      }
    } else {
      const h = content.scrollHeight;
      const anim = content.animate(
        [
          { height: `${h}px`, opacity: 1 },
          { height: '0px', opacity: 0 }
        ],
        { duration: Math.min(420, 180 + h * 0.3), easing: 'cubic-bezier(.65,0,.35,1)' }
      );
      anim.onfinish = () => {
        details.open = false;
        done();
      };
    }
  });

  /* ---------- Reveal on scroll ---------- */
  DL.initReveal = (root = document) => {
    const targets = DL.qsa('[data-reveal]:not(.is-in), [data-reveal-img]:not(.is-in)', root);
    if (!targets.length) return;
    if (!('IntersectionObserver' in window) || DL.reduceMotion || !document.documentElement.classList.contains('dl-anim')) {
      targets.forEach((t) => t.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    targets.forEach((t) => io.observe(t));
  };

  /* ---------- Predictive search ---------- */
  class DLPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      if (!this.input || !this.results) return;
      this.controller = null;
      this.input.addEventListener('input', DL.debounce(() => this.search(), 220));
      this.input.addEventListener('keydown', (e) => this.onKey(e));
    }
    async search() {
      const q = this.input.value.trim();
      if (!q) {
        this.results.innerHTML = '';
        return;
      }
      if (!DL.settings.predictiveSearch) return;
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      try {
        const url = `${DL.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,page,article&resources[limit]=6&resources[options][unavailable_products]=last&section_id=predictive-search`;
        const res = await fetch(url, { signal: this.controller.signal });
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('[data-predictive-content]');
        this.results.innerHTML = content ? content.innerHTML : '';
      } catch (err) {
        if (err.name !== 'AbortError') this.results.innerHTML = '';
      }
    }
    onKey(e) {
      const items = DL.qsa('[data-search-item]', this.results);
      if (!items.length || !['ArrowDown', 'ArrowUp'].includes(e.key)) return;
      e.preventDefault();
      const current = items.findIndex((i) => i.getAttribute('aria-selected') === 'true');
      items.forEach((i) => i.setAttribute('aria-selected', 'false'));
      let next = e.key === 'ArrowDown' ? current + 1 : current - 1;
      if (next < 0) next = items.length - 1;
      if (next >= items.length) next = 0;
      items[next].setAttribute('aria-selected', 'true');
      items[next].focus();
    }
  }
  if (!customElements.get('dl-predictive-search')) customElements.define('dl-predictive-search', DLPredictiveSearch);

  /* ---------- Boot ---------- */
  const boot = () => {
    DL.initReveal();
    document.addEventListener('shopify:section:load', (e) => DL.initReveal(e.target));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
